import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Which field's change log to show. A document placeholder is addressed by its
 * uuid + the document it sits in; a deal field by its own id.
 */
export type FieldHistoryTarget =
  | { scope: 'document'; title: string; placeholderUuid: string; linkableId: string }
  | { scope: 'dealField'; title: string; dealFieldGroupId: string };

interface FieldHistoryContextType {
  historyTarget: FieldHistoryTarget | null;
  openHistory: (target: FieldHistoryTarget) => void;
  closeHistory: () => void;
}

/**
 * Deliberately a working default rather than `null`.
 *
 * These cards render from more than one place, and a hook that throws would
 * turn "someone forgot a provider" into a blank placeholder sidebar. Without a
 * provider the History affordance is simply inert; the warning says so in
 * development.
 */
const FieldHistoryContext = createContext<FieldHistoryContextType>({
  historyTarget: null,
  openHistory: () => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[placeholders] History opened outside a FieldHistoryProvider — ignoring.');
    }
  },
  closeHistory: () => undefined,
});

/**
 * Opens the value-history slideout from either placeholder card.
 *
 * Same shape as {@link ContactPickerContext}: the item asks, the container owns
 * the panel. The slideout portals over the whole app, so mounting it per item
 * would stack one per expanded card.
 */
export const FieldHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [historyTarget, setHistoryTarget] = useState<FieldHistoryTarget | null>(null);

  const openHistory = useCallback((target: FieldHistoryTarget) => {
    setHistoryTarget(target);
  }, []);

  const closeHistory = useCallback(() => {
    setHistoryTarget(null);
  }, []);

  return (
    <FieldHistoryContext.Provider value={{ historyTarget, openHistory, closeHistory }}>
      {children}
    </FieldHistoryContext.Provider>
  );
};

export const useFieldHistory = () => useContext(FieldHistoryContext);
